import React, { forwardRef } from 'react';
import styled from 'styled-components';

import { getColor } from '../styles/themeGetters';
import { ColorKeys } from '../styles/themes';
import { Box } from '../Box';
import { marginMixin, MarginProps } from '../utils/mixins';

export type IconComponent = React.ComponentType<IconProps>;

type IconProps = MarginProps & {
  color?: ColorKeys;
  size?: string;
  noPadding?: boolean;
  className?: string;
  /** Names a meaningful icon. Without it the icon is hidden from assistive technology as decorative. */
  'aria-label'?: string;
};

/**
 * Use when you need to attach `onClick`s or a background to an Icon
 * @deprecated Use `Button` instead - with "s" size and
 */
export const IconBox = styled(Box)`
  line-height: 0;
  cursor: ${props => props.onClick && 'pointer'};
`;

/**
 * This is a workaround to support appropriate padding of the components.
 * TODO this should be improved, as the solution is not very efficient right now.
 *
 * NOTE: I'm guessing this solution was chosen as we currently use a 4px margin, on a default size of 24px.
 * As the size is variable, this means that the margin is 1/6th of the size, which makes it unpractical to be expressed as % (it would be 16,6 recurring).
 */
const PadIcon = styled(IconBox)`
  display: grid;
  grid-template-columns: 1fr 4fr 1fr;
  grid-template-rows: 1fr 4fr 1fr;
`;

/**
 * Wrapper to apply our preferred Icon interface to the generated SVG icons.
 * @param name name of the component, will be set as displayName
 * @param WrappedComponent the Icon to wrap
 */
export const withIconProps = (name: string, WrappedComponent) => {
  const UnpaddedIcon = styled(WrappedComponent)<MarginProps>`
    flex-shrink: 0;

    /* Overrides the global reset's vertical-align: middle for icons placed inline in text.
       It is the offset Font Awesome gave its icons, so icons that replaced Font Awesome
       ones keep their position next to the text. It has no effect on flex and grid items. */
    vertical-align: -0.125em;
    ${marginMixin}
  `;

  const Wrapper = forwardRef<SVGSVGElement, IconProps>(
    (
      {
        color = 'black',
        size = '24px',
        noPadding = false,
        className,
        'aria-label': ariaLabel,
        m,
        mx,
        my,
        mt,
        mb,
        ml,
        mr,
      },
      ref
    ) => {
      const marginProps = { m, mx, my, mt, mb, ml, mr };
      const accessibilityProps = ariaLabel
        ? { role: 'img', ariaLabel }
        : { ariaHidden: true };

      return noPadding ? (
        <UnpaddedIcon
          className={className}
          color={getColor(color)}
          width={size}
          height={size}
          ref={ref}
          {...accessibilityProps}
          {...marginProps}
        />
      ) : (
        <PadIcon
          className={className}
          width={size}
          height={size}
          {...marginProps}
        >
          {/* place the Icon in the middle of the PadIcon grid. Not a great solution, but we can refactor without changing the Api. */}
          <IconBox gridArea="2/2">
            <WrappedComponent
              color={getColor(color)}
              width="100%"
              height="100%"
              ref={ref}
              {...accessibilityProps}
            />
          </IconBox>
        </PadIcon>
      );
    }
  );
  Wrapper.displayName = name;
  return Wrapper;
};
